import React, { useState, useEffect } from 'react';
import {
  Box,
  Tabs,
  Tab,
  Typography,
  Grid,
  Button,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Snackbar,
  Alert,
  Autocomplete,
} from '@mui/material';
import axios from 'axios';
import StockTable from './StockTable';
import BeatLoader from 'react-spinners/BeatLoader';
import { BRAND } from '../constants/brand';

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`shop-tabpanel-${index}`}
      aria-labelledby={`shop-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          <Typography component={'span'}>{children}</Typography>
        </Box>
      )}
    </div>
  );
}

function StockReturn() {
  const [tabIndex, setTabIndex] = useState(0);
  const [shop1Stock, setShop1Stock] = useState([]);
  const [shop2Stock, setShop2Stock] = useState([]);
  const [shop3Stock, setShop3Stock] = useState([]);
  const [shop4Stock, setShop4Stock] = useState([]);
  const [storeStock, setStoreStock] = useState([]);

  const [loading, setLoading] = useState(false);

  const [shop1SearchTerm, setShop1SearchTerm] = useState('');
  const [shop2SearchTerm, setShop2SearchTerm] = useState('');
  const [shop3SearchTerm, setShop3SearchTerm] = useState('');
  const [shop4SearchTerm, setShop4SearchTerm] = useState('');

  const [selectedItem, setSelectedItem] = useState(null);
  const [allocationQty, setAllocationQty] = useState('');
  const [targetShop, setTargetShop] = useState('shop1');

  const [alertOpen, setAlertOpen] = useState(false);
  const [alertSeverity, setAlertSeverity] = useState('success');
  const [alertMessage, setAlertMessage] = useState('');

  // New state for handling quantity errors
  const [qtyError, setQtyError] = useState(false);
  const [qtyHelperText, setQtyHelperText] = useState('');

  useEffect(() => {
    fetchStockData();
  }, []);

  const shop = JSON.parse(sessionStorage.getItem(BRAND.sessionStorageKey))?.shop

  const fetchStockData = async () => {
    try {
      const [shop1Response, shop2Response, shop3Response, shop4Response, storeStockResponse] =
        await Promise.all([
          axios.get(
            'https://serveravotasautofleet.ccsoftwares.com/stock/getShop1Stock'
          ),
          axios.get(
            'https://serveravotasautofleet.ccsoftwares.com/stock/getShop2Stock'
          ),
          axios.get(
            'https://serveravotasautofleet.ccsoftwares.com/stock/getShop3Stock'
          ),
          axios.get(
            'https://serveravotasautofleet.ccsoftwares.com/stock/getShop4Stock'
          ),
          axios.get(
            'https://serveravotasautofleet.ccsoftwares.com/stock/getStoreStock'
          ),
        ]);

      setShop1Stock(shop1Response.data);
      setShop2Stock(shop2Response.data);
      setShop3Stock(shop3Response.data);
      setShop4Stock(shop4Response.data);
      setStoreStock(storeStockResponse.data);
    } catch (error) {
      console.error('Error fetching stock data:', error);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
  };

  const handleAllocation = async () => {
    if (!selectedItem || !allocationQty || !targetShop) {
      setAlertSeverity('warning');
      setAlertMessage('Please select item, quantity, and target shop.');
      setAlertOpen(true);
      return;
    }

    // Additional validation before allocation
    const allocationQtyValue = parseFloat(allocationQty);
    const availableQty = parseFloat(selectedItem.stock);

    if (!Number.isFinite(allocationQtyValue) || allocationQtyValue <= 0) {
      setAlertSeverity('error');
      setAlertMessage('Please enter a valid quantity.');
      setAlertOpen(true);
      return;
    }

    if (Number.isFinite(availableQty) && allocationQtyValue > availableQty) {
      setAlertSeverity('error');
      setAlertMessage(`Quantity cannot exceed available stock (${selectedItem.stock}).`);
      setAlertOpen(true);
      return;
    }

    const allocationData = {
      targetShop: targetShop,
      itemName: selectedItem.itemname,
      expirationDate: selectedItem.stockexpiredate,
      qty: allocationQtyValue,
      fromShop: shop === 'Ratnapura' ? 'Shop4_Stock' : shop === 'Kalawana' ? 'Shop2_Stock' : 'Shop3_Stock',
    };

    try {
      setLoading(true);
      await axios.post(
        `https://serveravotasautofleet.ccsoftwares.com/stock/stockAllocation`,
        allocationData
      );

      // Refresh stock data
      fetchStockData();
      setLoading(false);

      // Reset form fields
      setSelectedItem(null);
      setAllocationQty('');
      setTargetShop('shop1');

      setAlertSeverity('success');
      setAlertMessage('Stock allocated successfully.');
      setAlertOpen(true);
    } catch (error) {
      console.error('Error allocating stock:', error);
      setAlertSeverity('error');
      setAlertMessage('Error allocating stock.');
      setAlertOpen(true);
      setLoading(false);
    }
  };

  const handleAlertClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setAlertOpen(false);
  };

  // Filter functions for each shop
  const filterStockData = (stockData, searchTerm) => {
    return stockData.filter((item) =>
      item.itemname.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  // Handle quantity change with validation
  const handleQtyChange = (e) => {
    const value = e.target.value;
    setAllocationQty(value);

    if (selectedItem) {
      const maxQty = parseFloat(selectedItem.stock);
      const nextQty = parseFloat(value);
      if (
        value === '' ||
        !Number.isFinite(nextQty) ||
        !Number.isFinite(maxQty) ||
        nextQty <= maxQty
      ) {
        setQtyError(false);
        setQtyHelperText('');
      } else {
        setQtyError(true);
        setQtyHelperText(`Maximum available stock is ${maxQty}`);
      }
    }
  };

  return (
    <Box sx={{ width: '100%', paddingTop: '50px' }}>
      <Tabs value={tabIndex} onChange={handleTabChange} centered>
        {/* <Tab label="Main Store" />
        <Tab label="Kalawana Shop" />
        <Tab label="Pelmadulla Shop" />
        <Tab label="Ratnapura Shop" /> */}
        <Tab label="RETURN Stock" />
      </Tabs>

      {/* Shop 1 Stock */}
      <TabPanel value={tabIndex} index={10}>
        <Typography variant="h6">Main Store Stock</Typography>
        <TextField
          label="Search Item"
          variant="outlined"
          fullWidth
          sx={{ mt: 2, mb: 2 }}
          value={shop1SearchTerm}
          onChange={(e) => setShop1SearchTerm(e.target.value)}
        />
        <StockTable
          stockData={filterStockData(storeStock, shop1SearchTerm)}
        />
      </TabPanel>

      {/* Shop 2 Stock */}
      <TabPanel value={tabIndex} index={11}>
        <Typography variant="h6">Kalawana Stock</Typography>
        <TextField
          label="Search Item"
          variant="outlined"
          fullWidth
          sx={{ mt: 2, mb: 2 }}
          value={shop2SearchTerm}
          onChange={(e) => setShop2SearchTerm(e.target.value)}
        />
        <StockTable
          stockData={filterStockData(shop2Stock, shop2SearchTerm)}
        />
      </TabPanel>

      {/* Shop 3 Stock */}
      <TabPanel value={tabIndex} index={12}>
        <Typography variant="h6">Pelmadulla Stock</Typography>
        <TextField
          label="Search Item"
          variant="outlined"
          fullWidth
          sx={{ mt: 2, mb: 2 }}
          value={shop3SearchTerm}
          onChange={(e) => setShop3SearchTerm(e.target.value)}
        />
        <StockTable
          stockData={filterStockData(shop3Stock, shop3SearchTerm)}
        />
      </TabPanel>

      {/* Shop 4 Stock */}
      <TabPanel value={tabIndex} index={13}>
        <Typography variant="h6">Ratnapura Stock</Typography>
        <TextField
          label="Search Item"
          variant="outlined"
          fullWidth
          sx={{ mt: 2, mb: 2 }}
          value={shop4SearchTerm}
          onChange={(e) => setShop4SearchTerm(e.target.value)}
        />
        <StockTable
          stockData={filterStockData(shop4Stock, shop4SearchTerm)}
        />
      </TabPanel>

      {/* Allocate Stock */}
      <TabPanel value={tabIndex} index={0}>
        <Typography variant="h6">
          Return Stock {shop} To Main Store
        </Typography>
        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid item xs={12} sm={4}>

          {shop === 'Ratnapura' ? (
            <FormControl fullWidth>
                <Autocomplete
                options={shop4Stock}
                getOptionLabel={(option) =>
                    `${option.itemname} (${option.itemid})`
                }
                onChange={(event, newValue) => {
                    setSelectedItem(newValue);
                    // Reset quantity and errors when a new item is selected
                    setAllocationQty('');
                    setQtyError(false);
                    setQtyHelperText('');
                }}
                renderInput={(params) => (
                    <TextField {...params} label="Item" />
                )}
                filterOptions={(options, { inputValue }) =>
                    options.filter(
                    (option) =>
                        option.itemname
                        .toLowerCase()
                        .includes(inputValue.toLowerCase()) ||
                        option.itemid
                        .toString()
                        .toLowerCase()
                        .includes(inputValue.toLowerCase())
                    )
                }
                renderOption={(props, option) => (
                    <li {...props}>
                    <label style={{ fontWeight: 'bold' }}>
                        {option.itemname}
                    </label>{' '}
                    | EXP:{' '}
                    {option.stockexpiredate === '2100-12-31'
                        ? 'No'
                        : option.stockexpiredate}{' '}
                    | Avl: {option.stock}
                    </li>
                )}
                value={selectedItem}
                />
            </FormControl>
            ) : shop === 'Kalawana' ? (
            <FormControl fullWidth>
                <Autocomplete
                options={shop2Stock}
                getOptionLabel={(option) =>
                    `${option.itemname} (${option.itemid})`
                }
                onChange={(event, newValue) => {
                    setSelectedItem(newValue);
                    // Reset quantity and errors when a new item is selected
                    setAllocationQty('');
                    setQtyError(false);
                    setQtyHelperText('');
                }}
                renderInput={(params) => (
                    <TextField {...params} label="Item" />
                )}
                filterOptions={(options, { inputValue }) =>
                    options.filter(
                    (option) =>
                        option.itemname
                        .toLowerCase()
                        .includes(inputValue.toLowerCase()) ||
                        option.itemid
                        .toString()
                        .toLowerCase()
                        .includes(inputValue.toLowerCase())
                    )
                }
                renderOption={(props, option) => (
                    <li {...props}>
                    <label style={{ fontWeight: 'bold' }}>
                        {option.itemname}
                    </label>{' '}
                    | EXP:{' '}
                    {option.stockexpiredate === '2100-12-31'
                        ? 'No'
                        : option.stockexpiredate}{' '}
                    | Avl: {option.stock}
                    </li>
                )}
                value={selectedItem}
                />
            </FormControl>
            ) : (
                <FormControl fullWidth>
                <Autocomplete
                options={shop3Stock}
                getOptionLabel={(option) =>
                    `${option.itemname} (${option.itemid})`
                }
                onChange={(event, newValue) => {
                    setSelectedItem(newValue);
                    // Reset quantity and errors when a new item is selected
                    setAllocationQty('');
                    setQtyError(false);
                    setQtyHelperText('');
                }}
                renderInput={(params) => (
                    <TextField {...params} label="Item" />
                )}
                filterOptions={(options, { inputValue }) =>
                    options.filter(
                    (option) =>
                        option.itemname
                        .toLowerCase()
                        .includes(inputValue.toLowerCase()) ||
                        option.itemid
                        .toString()
                        .toLowerCase()
                        .includes(inputValue.toLowerCase())
                    )
                }
                renderOption={(props, option) => (
                    <li {...props}>
                    <label style={{ fontWeight: 'bold' }}>
                        {option.itemname}
                    </label>{' '}
                    | EXP:{' '}
                    {option.stockexpiredate === '2100-12-31'
                        ? 'No'
                        : option.stockexpiredate}{' '}
                    | Avl: {option.stock}
                    </li>
                )}
                value={selectedItem}
                />
            </FormControl>
            )}

          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Quantity"
              type="number"
              fullWidth
              value={allocationQty}
              onChange={handleQtyChange}
              error={qtyError}
              helperText={qtyHelperText}
              inputProps={{ min: 0.01, step: 0.01 }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth>
              <InputLabel id="select-shop-label">Allocate To</InputLabel>
              <Select
                labelId="select-shop-label"
                value={targetShop || "shop1"}
                label="Allocate To"
                onChange={(e) => setTargetShop(e.target.value)}
              >
                <MenuItem value="shop1">Main Stores</MenuItem>
                {/* Add more shops here if needed */}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
        <Button
          variant="contained"
          color="primary"
          sx={{ mt: 2 }}
          onClick={handleAllocation}
          disabled={loading || qtyError || !allocationQty || !selectedItem}
        >
          {loading ? (
            <>
              <BeatLoader color="#28968E" size={10} />
            </>
          ) : (
            <>Return stock</>
          )}
        </Button>
      </TabPanel>

      {/* Alert Snackbar */}
      <Snackbar
        open={alertOpen}
        autoHideDuration={6000}
        onClose={handleAlertClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleAlertClose}
          severity={alertSeverity}
          sx={{ width: '100%' }}
        >
          {alertMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default StockReturn;
